import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { Copy, Hash, Rows, Status } from 'regen-ui'
import * as z from 'zod/mini'

import { api } from '#/lib/api.js'
import * as Auth from '#/lib/auth.js'
import * as Bridge from '#/lib/bridge.js'
import { ScreenLayout } from '#/routes/_app/-components/ScreenLayout.js'

const searchSchema = z.looseObject({
  chainId: z.optional(z.union([z.number(), z.string()])),
  token: z.optional(z.string()),
})

const explorerUrls: Record<number, string> = {
  1: 'https://etherscan.io',
  10: 'https://optimistic.etherscan.io',
  130: 'https://uniscan.xyz',
  2741: 'https://abscan.org',
  42161: 'https://arbiscan.io',
  8453: 'https://basescan.org',
  [Bridge.solanaChainId]: 'https://solscan.io',
}

const chains = [Bridge.destinationChain, ...Bridge.sourceChains] as const

type RelayTx = {
  chainId?: number
  hash?: string
  status?: string
  timestamp?: number
}

type RelaySweepResponse = {
  requestId: string
  requests: {
    createdAt?: string
    depositAddress?: string
    depositTxHash?: string
    destinationFills: { chainId?: number; transactionId?: string }[]
    elapsedSeconds?: number
    failReason?: string
    id: string
    inTxs: RelayTx[]
    orderId?: string
    outTxs: RelayTx[]
    refunds: { chainId?: number; transactionId?: string }[]
    relaySettlement?: { chainId?: number; hash?: string; operation?: string }
    status: string
    supersededByRequestId?: string
    updatedAt?: string
  }[]
}

type DepositDebugData = {
  deposit: { address: string; id: string } | null
  depositError?: string | undefined
  relaySweeps: RelaySweepResponse | null
  relaySweepsError?: string | undefined
}

/** Wallet-shell help screen for debugging onchain deposits that have not appeared yet. */
export const Route = createFileRoute('/_app/deposit-faq')({
  component: Component,
  validateSearch: searchSchema,
})

function Component() {
  const search = Route.useSearch()
  const userAddress = Auth.useSessionAddress()
  const chainId = getChainId(search.chainId)
  const chain = chains.find((chain) => chain.id === chainId)
  const originChain = Bridge.sourceChains.find((chain) => chain.id === chainId)
  const token = getToken({ chain, token: search.token })
  const debug = useQuery({
    enabled: Boolean(userAddress && originChain && token),
    queryKey: Auth.session.queryKey(userAddress, 'deposit-debug', chainId, token?.address),
    queryFn: async (): Promise<DepositDebugData> => {
      const query = { chainId: String(chainId), token: token!.address }
      const depositRes = await api.api.bridge.deposit.$get({ query })

      if (depositRes.status === 404) return { deposit: null, relaySweeps: null }
      if (!depositRes.ok)
        return {
          deposit: null,
          depositError: await readError(depositRes),
          relaySweeps: null,
        }

      const deposit = (await depositRes.json()) as { address: string; id: string }
      const sweepsRes = await api.api.bridge.deposit.sweeps.$get({ query })
      if (!sweepsRes.ok)
        return {
          deposit,
          relaySweeps: null,
          relaySweepsError: await readError(sweepsRes),
        }

      return {
        deposit,
        relaySweeps: (await sweepsRes.json()) as RelaySweepResponse,
      }
    },
  })
  const data = debug.data ?? { deposit: null, relaySweeps: null }
  const explorerUrl = chainId ? explorerUrls[chainId] : undefined
  const depositExplorerUrl = getExplorerAddressUrl({
    address: data.deposit?.address,
    chainId,
    explorerUrl,
  })
  const supportHref = getSupportHref({
    chainName: chain?.name ?? (chainId ? `Chain ${chainId}` : undefined),
    depositAddress: data.deposit?.address,
    relayRequestId: data.deposit?.id,
    token: token?.symbol ?? search.token,
    userAddress,
  })

  return (
    <ScreenLayout heading={<h1 className="text-heading-24">Where are my funds?</h1>}>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="flex flex-col gap-5">
          <Rows>
            <Rows.Row label="Network">
              <span className="text-label-13 text-foreground-secondary">
                {chain?.name ?? (chainId ? `Chain ${chainId}` : 'Not selected')}
              </span>
            </Rows.Row>
            <Rows.Row label="Token">
              <span className="text-label-13 text-foreground-secondary">
                {token?.symbol ?? search.token ?? 'Not selected'}
              </span>
            </Rows.Row>
          </Rows>

          {debug.isError ? (
            <InfoCard title="Unable to load deposit details" type="error">
              Please try again.
            </InfoCard>
          ) : debug.isLoading ? (
            <InfoCard loading title="Loading deposit details" />
          ) : data.depositError ? (
            <InfoCard title="Relay details" type="error">
              {data.depositError}
            </InfoCard>
          ) : data.deposit ? (
            <>
              <Rows>
                <Rows.Row label="Deposit address">
                  <div className="flex min-w-0 items-center gap-2">
                    <Hash
                      className="min-w-0 text-label-13 text-foreground-secondary"
                      isEvm={Bridge.getNamespace(chainId!) === 'eip155'}
                      value={data.deposit.address}
                    />
                    <Copy size="xs" title="Copy deposit address" value={data.deposit.address} />
                  </div>
                </Rows.Row>
              </Rows>
              <RelaySweepDetails data={data.relaySweeps} error={data.relaySweepsError} />
            </>
          ) : (
            <InfoCard title="No Relay route" />
          )}
        </div>

        <aside className="flex flex-col gap-4 self-start">
          <h2 className="text-label-16 text-foreground">Need help?</h2>
          <a
            className="text-label-14 text-foreground underline decoration-border underline-offset-2 hover:decoration-foreground focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
            href={supportHref}
          >
            support@tempo.xyz
          </a>
          {depositExplorerUrl ? (
            <a
              className="rounded-body border border-border bg-surface p-3 text-label-14 text-foreground transition-[background-color,border-color] hover:border-border-hover hover:bg-surface-hover focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
              href={depositExplorerUrl}
              rel="noreferrer"
              target="_blank"
            >
              Open network explorer
            </a>
          ) : null}
        </aside>
      </div>
    </ScreenLayout>
  )
}

function RelaySweepDetails(props: { data: RelaySweepResponse | null; error?: string | undefined }) {
  const { data, error } = props
  if (error)
    return (
      <InfoCard title="Relay sweep lookup" type="error">
        {error}
      </InfoCard>
    )

  const requests = data?.requests ?? []
  if (requests.length === 0) return <InfoCard title="No Relay sweeps" />

  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-label-16 text-foreground">Sweep details</h2>
      {requests.map((request) => (
        <RelayRequestRows key={request.id} request={request} />
      ))}
    </div>
  )
}

function RelayRequestRows(props: { request: RelaySweepResponse['requests'][number] }) {
  const { request } = props
  const sourceTxs = request.inTxs.filter((tx) => tx.hash && tx.hash !== request.depositTxHash)
  const fills = [
    ...request.destinationFills.map((fill) => ({
      chainId: fill.chainId,
      hash: fill.transactionId,
    })),
    ...request.outTxs.map((tx) => ({ chainId: tx.chainId, hash: tx.hash })),
  ].filter((tx) => tx.hash)
  const refunds = request.refunds.filter((refund) => refund.transactionId)

  return (
    <Rows>
      <Rows.Row label="Status">
        <span className="text-label-13 capitalize text-foreground-secondary">{request.status}</span>
      </Rows.Row>
      {typeof request.elapsedSeconds === 'number' ? (
        <Rows.Row label="Elapsed">
          <span className="text-label-13 text-foreground-secondary">
            {formatElapsed(request.elapsedSeconds)}
          </span>
        </Rows.Row>
      ) : null}
      <Rows.Row label="Request id">
        <CopyableHash value={request.id} />
      </Rows.Row>
      {request.supersededByRequestId ? (
        <Rows.Row label="Superseded by">
          <CopyableHash value={request.supersededByRequestId} />
        </Rows.Row>
      ) : null}
      {request.depositTxHash ? (
        <Rows.Row label="Deposit tx">
          <CopyableHash value={request.depositTxHash} />
        </Rows.Row>
      ) : null}
      {sourceTxs.length > 0 ? (
        <Rows.Row label="Source txs">
          <div className="flex min-w-0 flex-col gap-2">
            {sourceTxs.map((tx, index) => (
              <TxValue key={`${tx.hash}-${index}`} chainId={tx.chainId} hash={tx.hash!} />
            ))}
          </div>
        </Rows.Row>
      ) : null}
      {request.relaySettlement?.hash ? (
        <Rows.Row label="Relay settlement">
          <TxValue chainId={request.relaySettlement.chainId} hash={request.relaySettlement.hash} />
        </Rows.Row>
      ) : null}
      {fills.length > 0 ? (
        <Rows.Row label="Sweeps / fills">
          <div className="flex min-w-0 flex-col gap-2">
            {fills.map((tx, index) => (
              <TxValue key={`${tx.hash}-${index}`} chainId={tx.chainId} hash={tx.hash!} />
            ))}
          </div>
        </Rows.Row>
      ) : null}
      {refunds.length > 0 ? (
        <Rows.Row label="Refunds">
          <div className="flex min-w-0 flex-col gap-2">
            {refunds.map((refund, index) => (
              <TxValue
                key={`${refund.transactionId}-${index}`}
                chainId={refund.chainId}
                hash={refund.transactionId!}
              />
            ))}
          </div>
        </Rows.Row>
      ) : null}
      {request.failReason ? (
        <Rows.Row label="Failure reason">
          <span className="text-label-13 text-foreground-secondary">{request.failReason}</span>
        </Rows.Row>
      ) : null}
      {request.updatedAt ? (
        <Rows.Row label="Last updated">
          <span className="text-label-13 text-foreground-secondary">
            {new Date(request.updatedAt).toLocaleString()}
          </span>
        </Rows.Row>
      ) : null}
    </Rows>
  )
}

function TxValue(props: { chainId: number | undefined; hash: string }) {
  const { chainId, hash } = props
  const explorerUrl = chainId ? explorerUrls[chainId] : undefined
  const href = explorerUrl ? `${explorerUrl}/tx/${hash}` : undefined
  const content = (
    <CopyableHash isEvm={chainId ? Bridge.getNamespace(chainId) === 'eip155' : true} value={hash} />
  )
  if (!href) return content
  return (
    <a
      className="min-w-0 underline decoration-border underline-offset-2 hover:decoration-foreground focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
      href={href}
      rel="noreferrer"
      target="_blank"
    >
      {content}
    </a>
  )
}

function CopyableHash(props: { isEvm?: boolean | undefined; value: string }) {
  const { isEvm = true, value } = props
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Hash
        className="min-w-0 text-label-13 text-foreground-secondary"
        isEvm={isEvm}
        value={value}
      />
      <Copy size="xs" title="Copy" value={value} />
    </div>
  )
}

function InfoCard(props: {
  children?: ReactNode | undefined
  loading?: boolean | undefined
  title: string
  type?: 'error' | 'info' | undefined
}) {
  const { children, loading, title, type = 'info' } = props
  return (
    <div className="flex gap-3 rounded-body border border-border bg-surface p-4">
      <Status type={loading ? 'loading' : type} />
      <div className="flex flex-col gap-1">
        <p className="text-label-14 text-foreground">{title}</p>
        {children ? <p className="text-copy-13 text-foreground-secondary">{children}</p> : null}
      </div>
    </div>
  )
}

function getChainId(value: string | number | undefined): number | undefined {
  if (typeof value === 'number' && Number.isInteger(value)) return value
  if (typeof value !== 'string') return undefined
  const parsed = Number(value)
  if (!Number.isInteger(parsed)) return undefined
  return parsed
}

function getToken(options: {
  chain: (typeof chains)[number] | undefined
  token: string | undefined
}): Bridge.Token | undefined {
  const { chain, token } = options
  if (!chain || !token) return undefined
  const token_lower = token.toLowerCase()
  return chain.tokens.find((token_) => {
    if (token_.symbol.toLowerCase() === token_lower) return true
    return token_.address.toLowerCase() === token_lower
  })
}

function getExplorerAddressUrl(options: {
  address: string | undefined
  chainId: number | undefined
  explorerUrl: string | undefined
}) {
  const { address, chainId, explorerUrl } = options
  if (!address || !chainId || !explorerUrl) return undefined
  if (chainId === Bridge.solanaChainId) return `${explorerUrl}/account/${address}`
  return `${explorerUrl}/address/${address}`
}

function getSupportHref(options: {
  chainName: string | undefined
  depositAddress: string | undefined
  relayRequestId: string | undefined
  token: string | undefined
  userAddress: string | undefined
}) {
  const fields = [
    ['User address', options.userAddress],
    ['Network', options.chainName],
    ['Token', options.token],
    ['Deposit address', options.depositAddress],
    ['Request id', options.relayRequestId],
    ['Transaction hash', ''],
  ] as const
  const body = fields.map(([label, value]) => `${label}: ${value ?? ''}`).join('\n')
  return `mailto:support@tempo.xyz?subject=${encodeURIComponent('Missing deposit')}&body=${encodeURIComponent(body)}`
}

function formatElapsed(seconds: number) {
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (minutes < 60) return remainingSeconds ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`
}

async function readError(res: { json: () => Promise<unknown>; status: number }) {
  const fallback = `Request failed with ${res.status}`
  try {
    const body = (await res.json()) as { error?: string; message?: string }
    return body.message ?? body.error ?? fallback
  } catch {
    return fallback
  }
}
